import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LibraryBig, Plus, X, Loader2, Trash2, ExternalLink, Youtube, Download, UploadCloud, Wand2, Pencil } from "lucide-react";
import { API, authHeaders, fileUrl, uploadAdminFile } from "@/constants/roles";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const ASSET_CATS = ["models", "audio", "graphics", "thumbnails", "fonts", "references", "other"];

const Tag = ({ children }) => (
  <span className="rounded-full bg-[var(--pv-subtle)] px-2 py-0.5 text-[10px] font-semibold text-[var(--pv-text-3)]">{children}</span>
);

// ==================== COMPETITORS TAB ====================
const CompetitorsTab = ({ canDelete }) => {
  const [items, setItems] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", url: "", notes: "", tags: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/staff/competitors`, { headers: authHeaders() }).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, url: c.url || "", notes: c.notes || "", tags: (c.tags || []).join(", ") });
    setShowAdd(true);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name, url: form.url, notes: form.notes,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editId) {
        await axios.patch(`${API}/staff/competitors/${editId}`, payload, { headers: authHeaders() });
        toast.success("Competitor updated.");
      } else {
        await axios.post(`${API}/staff/competitors`, payload, { headers: authHeaders() });
        toast.success("Competitor added.");
      }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: "", url: "", notes: "", tags: "" });
      load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : editId ? "Could not update competitor." : "Could not add competitor."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this competitor? Their reference videos stay, unlinked.")) return;
    await axios.delete(`${API}/staff/competitors/${id}`, { headers: authHeaders() }).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div>
      <div className="flex justify-end">
        <button data-testid="competitor-add-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add competitor
        </button>
      </div>
      {showAdd && (
        <form data-testid="competitor-add-form" onSubmit={add} className="mt-4 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5 sm:grid-cols-2">
          <input data-testid="competitor-name-input" required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Channel name" className={inputCls} />
          <input data-testid="competitor-url-input" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="Channel URL" className={inputCls} />
          <input data-testid="competitor-tags-input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className={inputCls} />
          <input data-testid="competitor-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes — what do they do well?" className={inputCls} />
          <div className="flex items-center gap-2">
            <button data-testid="competitor-save-btn" type="submit" disabled={busy} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {editId ? "Save changes" : "Save competitor"}
            </button>
            {editId && (
              <button type="button" data-testid="competitor-cancel-edit" onClick={() => { setEditId(null); setShowAdd(false); setForm({ name: "", url: "", notes: "", tags: "" }); }} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)]">Cancel edit</button>
            )}
          </div>
        </form>
      )}
      {!items ? (
        <p className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : items.length === 0 ? (
        <p data-testid="competitors-empty" className="mt-4 rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">No competitors tracked yet — add the channels you want to out-publish.</p>
      ) : (
        <div data-testid="competitors-grid" className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`competitor-card-${c.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
              <div className="flex items-start justify-between gap-2">
                <p className="font-display text-base font-bold text-[var(--pv-text)]">{c.name}</p>
                <div className="flex items-center gap-1">
                  {c.can_edit && (
                    <button data-testid={`competitor-edit-${c.id}`} onClick={() => startEdit(c)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-cyan-500/10 hover:text-cyan-400" title="Edit (entries you created)"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                  {canDelete && (
                    <button data-testid={`competitor-delete-${c.id}`} onClick={() => remove(c.id)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
              {c.url && (
                <a href={c.url} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1 text-xs font-semibold text-cyan-400 hover:text-cyan-300">
                  <ExternalLink className="h-3 w-3" /> {c.url.replace(/^https?:\/\//, "").slice(0, 40)}
                </a>
              )}
              {c.notes && <p className="mt-2 line-clamp-3 text-xs text-[var(--pv-text-4)]">{c.notes}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {(c.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}
                <span className="rounded-full bg-cyan-500/10 border border-cyan-500/25 px-2 py-0.5 text-[10px] font-bold text-cyan-300">{c.video_count} ref{c.video_count === 1 ? "" : "s"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== REFERENCE VIDEOS TAB ====================
const ReferencesTab = ({ canDelete }) => {
  const [items, setItems] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ url: "", title: "", notes: "", competitor_id: "", tags: "" });
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/staff/references`, { headers: authHeaders() }).then((r) => setItems(r.data)).catch(() => {});
    axios.get(`${API}/staff/competitors`, { headers: authHeaders() }).then((r) => setCompetitors(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({ url: r.url || "", title: r.title || "", notes: r.notes || "", competitor_id: r.competitor_id || "", tags: (r.tags || []).join(", ") });
    setMeta(null);
    setShowAdd(true);
  };

  const resetForm = () => {
    setEditId(null);
    setShowAdd(false);
    setForm({ url: "", title: "", notes: "", competitor_id: "", tags: "" });
    setMeta(null);
  };

  const lookup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/staff/references/lookup`, { url: form.url }, { headers: authHeaders() });
      setMeta(r.data);
      setForm((f) => ({ ...f, title: f.title || r.data.title }));
      toast.success(`Found: ${r.data.title.slice(0, 60)}`);
    } catch (err) { setMeta(null); toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Lookup failed."); }
    finally { setBusy(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        url: form.url, title: form.title || meta?.title || "", notes: form.notes,
        competitor_id: form.competitor_id || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editId) {
        await axios.patch(`${API}/staff/references/${editId}`, payload, { headers: authHeaders() });
        toast.success("Reference updated.");
      } else {
        await axios.post(`${API}/staff/references`, payload, { headers: authHeaders() });
        toast.success("Reference video saved.");
      }
      resetForm();
      load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : editId ? "Could not update reference." : "Could not save reference."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reference?")) return;
    await axios.delete(`${API}/staff/references/${id}`, { headers: authHeaders() }).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div>
      <div className="flex justify-end">
        <button data-testid="reference-add-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add reference video
        </button>
      </div>
      {showAdd && (
        <form data-testid="reference-add-form" onSubmit={add} className="mt-4 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5 sm:grid-cols-2">
          <div className="flex gap-2 sm:col-span-2">
            <input data-testid="reference-url-input" required value={form.url} onChange={(e) => { setForm((f) => ({ ...f, url: e.target.value })); setMeta(null); }} placeholder="YouTube video URL (watch or shorts)" className={inputCls} />
            <button type="button" data-testid="reference-lookup-btn" onClick={lookup} disabled={busy || !form.url} className="flex shrink-0 items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Fetch details
            </button>
          </div>
          {meta && (
            <div data-testid="reference-lookup-result" className="flex items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-3 sm:col-span-2">
              {meta.thumbnail && <img src={meta.thumbnail} alt="" className="h-14 w-24 rounded-lg object-cover" />}
              <div className="min-w-0 text-xs">
                <p className="truncate font-bold text-[var(--pv-text)]">{meta.title}</p>
                <p className="text-[var(--pv-text-4)]">{meta.channel} · {meta.views.toLocaleString()} views</p>
              </div>
            </div>
          )}
          <input data-testid="reference-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (auto-filled by lookup)" className={inputCls} />
          <select data-testid="reference-competitor-input" value={form.competitor_id} onChange={(e) => setForm((f) => ({ ...f, competitor_id: e.target.value }))} className={inputCls}>
            <option value="">No competitor link</option>
            {competitors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input data-testid="reference-tags-input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className={inputCls} />
          <input data-testid="reference-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Why does this video work?" className={inputCls} />
          <button data-testid="reference-save-btn" type="submit" disabled={busy} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {editId ? "Save changes" : "Save reference"}
          </button>
          {editId && (
            <button type="button" data-testid="reference-cancel-edit" onClick={resetForm} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)]">Cancel edit</button>
          )}
        </form>
      )}
      {!items ? (
        <p className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : items.length === 0 ? (
        <p data-testid="references-empty" className="mt-4 rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">No reference videos yet — save YouTube videos worth studying.</p>
      ) : (
        <div data-testid="references-list" className="mt-4 space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`reference-row-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-5 py-4">
              <Youtube className="h-5 w-5 shrink-0 text-rose-400" />
              <div className="min-w-0 flex-1">
                <a href={r.url} target="_blank" rel="noreferrer" className="text-sm font-bold text-[var(--pv-text)] hover:text-cyan-400">{r.title || r.url}</a>
                <p className="text-[11px] text-[var(--pv-text-4)]">
                  {[r.competitor_name ? `competitor: ${r.competitor_name}` : null, r.notes ? r.notes.slice(0, 80) : null].filter(Boolean).join(" · ")}
                </p>
              </div>
              {(r.tags || []).slice(0, 3).map((t) => <Tag key={t}>{t}</Tag>)}
              {r.can_edit && (
                <button data-testid={`reference-edit-${r.id}`} onClick={() => startEdit(r)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-cyan-500/10 hover:text-cyan-400" title="Edit (entries you created)"><Pencil className="h-3.5 w-3.5" /></button>
              )}
              {canDelete && (
                <button data-testid={`reference-delete-${r.id}`} onClick={() => remove(r.id)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ASSETS TAB ====================
const AssetsTab = ({ canDelete }) => {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", category: "models", url: "", notes: "", tags: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const qs = cat ? `?category=${cat}` : "";
    axios.get(`${API}/staff/assets${qs}`, { headers: authHeaders() }).then((r) => setItems(r.data)).catch(() => {});
  }, [cat]);
  useEffect(load, [load]);

  const startEdit = (a) => {
    setEditId(a.id);
    setForm({ name: a.name, category: a.category, url: a.url || "", notes: a.notes || "", tags: (a.tags || []).join(", ") });
    setFile(null);
    setShowAdd(true);
  };

  const resetForm = () => {
    setEditId(null);
    setShowAdd(false);
    setForm({ name: "", category: "models", url: "", notes: "", tags: "" });
    setFile(null);
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let file_path = "";
      if (file) {
        const up = await uploadAdminFile(file);
        file_path = up.path;
      }
      const payload = {
        name: form.name, category: form.category, url: form.url,
        notes: form.notes, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editId) {
        if (file_path) payload.file_path = file_path;
        await axios.patch(`${API}/staff/assets/${editId}`, payload, { headers: authHeaders() });
        toast.success("Asset updated.");
      } else {
        await axios.post(`${API}/staff/assets`, { ...payload, file_path }, { headers: authHeaders() });
        toast.success("Asset added to the library.");
      }
      resetForm();
      load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : editId ? "Could not update asset." : "Could not add asset."); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset record?")) return;
    await axios.delete(`${API}/staff/assets/${id}`, { headers: authHeaders() }).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div data-testid="asset-category-pills" className="flex flex-wrap gap-1.5">
          {["", ...ASSET_CATS].map((c) => (
            <button key={c || "all"} data-testid={`asset-cat-${c || "all"}`} onClick={() => setCat(c)}
              className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold capitalize transition-colors ${cat === c ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)]"}`}>
              {c || "All"}
            </button>
          ))}
        </div>
        <button data-testid="asset-add-btn" onClick={() => setShowAdd((v) => !v)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add asset
        </button>
      </div>
      {showAdd && (
        <form data-testid="asset-add-form" onSubmit={add} className="mt-4 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5 sm:grid-cols-2">
          <input data-testid="asset-name-input" required minLength={2} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Asset name" className={inputCls} />
          <select data-testid="asset-category-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls}>
            {ASSET_CATS.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
          <div className="flex items-center gap-2 sm:col-span-2">
            <button type="button" data-testid="asset-file-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-cyan-400">
              <UploadCloud className="h-3.5 w-3.5" /> {file ? file.name : "Upload file"}
            </button>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <span className="text-[10px] font-bold uppercase text-[var(--pv-text-4)]">or</span>
            <input data-testid="asset-url-input" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="External URL" className={inputCls} />
          </div>
          <input data-testid="asset-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes" className={inputCls} />
          <input data-testid="asset-tags-input" value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className={inputCls} />
          <button data-testid="asset-save-btn" type="submit" disabled={busy || (!editId && !file && !form.url)} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {editId ? "Save changes" : "Save asset"}
          </button>
          {editId && (
            <button type="button" data-testid="asset-cancel-edit" onClick={resetForm} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)]">Cancel edit</button>
          )}
        </form>
      )}
      {!items ? (
        <p className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : items.length === 0 ? (
        <p data-testid="assets-empty" className="mt-4 rounded-3xl border border-dashed border-[var(--pv-border-strong)] py-14 text-center text-sm text-[var(--pv-text-4)]">No assets in this category yet.</p>
      ) : (
        <div data-testid="assets-grid" className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`asset-card-${a.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-bold text-[var(--pv-text)]">{a.name}</p>
                <div className="flex items-center gap-1">
                  {a.can_edit && (
                    <button data-testid={`asset-edit-${a.id}`} onClick={() => startEdit(a)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-cyan-500/10 hover:text-cyan-400" title="Edit (entries you created)"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                  {canDelete && (
                    <button data-testid={`asset-delete-${a.id}`} onClick={() => remove(a.id)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              </div>
              <span className="mt-1.5 inline-block rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-3)]">{a.category}</span>
              {a.notes && <p className="mt-2 line-clamp-2 text-xs text-[var(--pv-text-4)]">{a.notes}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {a.file_path && (
                  <a data-testid={`asset-download-${a.id}`} href={fileUrl(a.file_path)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 px-2.5 py-1 text-[10px] font-bold text-cyan-300 hover:bg-cyan-500/20">
                    <Download className="h-3 w-3" /> Download
                  </a>
                )}
                {a.url && (
                  <a href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] px-2.5 py-1 text-[10px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400">
                    <ExternalLink className="h-3 w-3" /> Open link
                  </a>
                )}
                {(a.tags || []).slice(0, 2).map((t) => <Tag key={t}>{t}</Tag>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== LIBRARY SHELL ====================
export const AdminLibrary = ({ canDelete = false }) => {
  const [tab, setTab] = useState("competitors");
  const tabs = [
    { id: "competitors", label: "Competitors" },
    { id: "references", label: "Reference videos" },
    { id: "assets", label: "Assets" },
  ];
  return (
    <div data-testid="library-section">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
          <LibraryBig className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Library</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Competitors to watch, videos worth studying, and reusable production assets.</p>
        </div>
      </div>
      <div data-testid="library-tabs" className="mt-5 flex gap-2 border-b border-[var(--pv-border)] pb-px">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`library-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`rounded-t-xl px-4 py-2.5 text-xs font-bold transition-colors ${tab === t.id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-5">
        {tab === "competitors" && <CompetitorsTab canDelete={canDelete} />}
        {tab === "references" && <ReferencesTab canDelete={canDelete} />}
        {tab === "assets" && <AssetsTab canDelete={canDelete} />}
      </div>
    </div>
  );
};
